import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircle,
  ArrowRight,
  Shield,
  BarChart3,
  MessageSquare,
  Award,
  GraduationCap,
  Smartphone,
  Trophy,
  ChevronDown,
  Heart
} from 'lucide-react'
import { AnimatedLogo, RippleCard } from '../../components/healthfluence'

// Stats for the About section
const stats = [
  { value: '$8.5B', label: 'Healthcare Influencer Market by 2028', icon: BarChart3 },
  { value: '73%', label: 'Patients Trust Physician Recommendations', icon: Heart },
  { value: '4M+', label: 'Combined Founder Social Reach', icon: Smartphone },
  { value: '100%', label: 'Compliance-First Approach', icon: Shield }
]

// Core values with expandable content
const values = [
  {
    id: 'trust',
    title: 'Trust Through Verification',
    icon: CheckCircle,
    short: 'Every influencer is credential-verified before joining the platform.',
    expanded: "We verify medical licenses, board certifications, and professional standing for every healthcare influencer. Brands never have to worry about working with unqualified voices. This is the foundation of everything we do."
  },
  {
    id: 'compliance',
    title: 'Compliance as a Feature',
    icon: Shield,
    short: 'FDA, FTC, and HIPAA compliance built into every workflow.',
    expanded: "Healthcare marketing has unique regulatory requirements. Our platform includes automated disclosure insertion, medical claim review, and complete audit trails. Compliance isn't an afterthought—it's a competitive advantage."
  },
  {
    id: 'authenticity',
    title: 'Authentic Voices Only',
    icon: MessageSquare,
    short: 'Real physicians sharing real expertise with their communities.',
    expanded: "We believe the most powerful healthcare marketing comes from authentic physician voices—not actors or spokespeople. Our platform connects brands with doctors who genuinely believe in the products they recommend."
  },
  {
    id: 'impact',
    title: 'Measurable Impact',
    icon: BarChart3,
    short: 'Full attribution tracking and ROI measurement.',
    expanded: "Every campaign includes comprehensive analytics: engagement metrics, conversion tracking, audience demographics, and ROI calculations. Brands see exactly what they're getting, and influencers understand their true value."
  }
]

// Company timeline
const timeline = [
  {
    year: '2020',
    title: 'The Spark',
    description: 'Dr. Betsy Grunch begins sharing medical education on TikTok during the pandemic, quickly building a following of millions seeking trustworthy health information.'
  },
  {
    year: '2023',
    title: 'The Problem',
    description: 'Despite massive demand, no platform exists to connect healthcare brands with verified medical influencers while ensuring regulatory compliance.'
  },
  {
    year: '2024',
    title: 'The Vision',
    description: 'Dr. Grunch partners with healthcare technology innovators to design a compliance-first influencer marketplace built specifically for healthcare.'
  },
  {
    year: '2025',
    title: 'Healthfluence Launches',
    description: 'The first healthcare influencer marketplace with built-in credential verification, compliance workflows, and managed campaign services goes live.'
  }
]

// Dr. Grunch credentials
const credentials = [
  { label: 'Board Certified', value: 'Neurosurgeon', icon: Award },
  { label: 'Fellowship', value: 'Duke University', icon: GraduationCap },
  { label: 'Social Reach', value: '4M+ Followers', icon: Smartphone },
  { label: 'Recognition', value: "Newsweek's Best", icon: Trophy }
]

// Dr. Grunch accolades
const accolades = [
  "America's Best Spine Surgeons 2024 — Newsweek",
  "Top Doctor — Georgia Trend Magazine (2020-2023)",
  "Top Doctor — Atlanta Magazine (2022-2025)",
  "Women Making a Mark — Atlanta Magazine 2023",
  "Fellow, American College of Surgeons (FACS)",
  "Fellow, American Association of Neurological Surgeons (FAANS)"
]

// Dr. Grunch quotes
const quotes = [
  {
    text: "Healthcare marketing needs voices patients can actually trust. Not actors, not spokespeople—real physicians who understand the weight of what we recommend.",
    context: "On founding Healthfluence"
  },
  {
    text: "I've seen firsthand how social media can combat misinformation and genuinely help people make better health decisions. But it has to be done right—with compliance, authenticity, and real medical expertise.",
    context: "On the mission"
  },
  {
    text: "Representation matters. When I was in training, I didn't see many women who looked like me in neurosurgery. Now I can use my platform to show the next generation what's possible—and Healthfluence lets other physicians do the same.",
    context: "On physician influencers"
  }
]

function AboutPage() {
  const [expandedValue, setExpandedValue] = useState(null)
  const [activeTab, setActiveTab] = useState('story')

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-hf-teal-600 via-hf-teal-700 to-hf-navy-700 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-8">
            <AnimatedLogo size="lg" variant="light" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Healthcare Marketing<br />Built on Trust
          </h1>
          <p className="text-xl text-hf-teal-100 max-w-3xl mx-auto leading-relaxed">
            Healthfluence is the first influencer marketplace designed exclusively for healthcare—where verified medical professionals connect with brands to create compliant, authentic campaigns that patients actually trust.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-hf-teal-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-hf-teal-600" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-hf-teal-600 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Mission */}
            <RippleCard className="bg-white p-8 rounded-2xl">
              <span className="inline-block px-3 py-1 bg-hf-teal-100 text-hf-teal-700 rounded-md text-xs font-semibold uppercase tracking-wide mb-4">
                Our Mission
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-5">
                Empower Healthcare Brands to Reach Patients Through Voices They Trust
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                We connect healthcare companies with verified medical professionals who have built authentic relationships with their audiences. By ensuring every partnership meets regulatory standards, we make compliant healthcare marketing not just possible—but effortless.
              </p>
            </RippleCard>

            {/* Vision */}
            <RippleCard className="bg-white p-8 rounded-2xl">
              <span className="inline-block px-3 py-1 bg-hf-teal-100 text-hf-teal-700 rounded-md text-xs font-semibold uppercase tracking-wide mb-4">
                Our Vision
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-5">
                A World Where Health Information Comes From Trusted Experts
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                We envision a future where misinformation loses to expert voices—where patients can trust the health content they see because it comes from verified professionals who stake their reputation on accuracy. Healthfluence makes that future a reality.
              </p>
            </RippleCard>
          </div>
        </div>
      </section>

      {/* Why Healthfluence Exists */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Healthfluence Exists
            </h2>
            <p className="text-xl text-gray-600">
              Healthcare marketing is broken. Here's what we're fixing.
            </p>
          </div>

          {/* Problem Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-red-50 rounded-2xl p-6 border-l-4 border-red-500">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-red-600 text-xl">✕</span>
              </div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">The Trust Gap</h3>
              <p className="text-red-800 text-sm leading-relaxed">
                Patients don't trust traditional healthcare advertising. Celebrity endorsements and actor testimonials feel inauthentic and often misleading.
              </p>
            </div>

            <div className="bg-red-50 rounded-2xl p-6 border-l-4 border-red-500">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-red-600 text-xl">⚠</span>
              </div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">The Compliance Maze</h3>
              <p className="text-red-800 text-sm leading-relaxed">
                FDA, FTC, and HIPAA regulations make healthcare marketing complex. One mistake can mean massive fines or damaged reputations.
              </p>
            </div>

            <div className="bg-red-50 rounded-2xl p-6 border-l-4 border-red-500">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-red-600 text-xl">?</span>
              </div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">The Discovery Problem</h3>
              <p className="text-red-800 text-sm leading-relaxed">
                Brands struggle to find legitimate physician influencers. Influencers struggle to find brand partnerships that align with their values.
              </p>
            </div>
          </div>

          {/* Arrow */}
          <div className="text-center text-3xl text-gray-300 mb-10">↓</div>

          {/* Solution */}
          <div className="bg-gradient-to-br from-hf-teal-50 to-healthcare-teal/10 rounded-3xl p-8 lg:p-12 text-center border-2 border-hf-teal-200">
            <div className="w-16 h-16 bg-hf-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-hf-teal-600" />
            </div>
            <h3 className="text-2xl font-bold text-hf-teal-700 mb-4">
              Healthfluence: The Solution
            </h3>
            <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
              A closed marketplace where every influencer is credential-verified, every campaign is compliance-reviewed, and every partnership is built on authentic expertise. We handle the complexity so brands and physicians can focus on what matters: helping patients.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide every decision we make.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {values.map((value) => (
              <button
                key={value.id}
                onClick={() => setExpandedValue(expandedValue === value.id ? null : value.id)}
                className={`bg-white rounded-2xl p-6 text-left cursor-pointer transition-all duration-300 border-2 ${
                  expandedValue === value.id
                    ? 'border-hf-teal-500 shadow-lg shadow-hf-teal-500/10'
                    : 'border-transparent shadow-sm hover:shadow-md'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-hf-teal-500 to-hf-teal-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <value.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {expandedValue === value.id ? value.expanded : value.short}
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ${
                      expandedValue === value.id ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-hf-teal-500 to-healthcare-teal" />

            {timeline.map((item, i) => (
              <div key={i} className="flex gap-8 mb-10 last:mb-0 relative">
                {/* Year bubble */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-hf-teal-500 to-healthcare-teal flex items-center justify-center text-white text-sm font-bold flex-shrink-0 z-10">
                  {item.year.slice(2)}
                </div>

                {/* Content */}
                <div className="pt-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dr. Grunch Section */}
      <section className="py-20 bg-gradient-to-b from-white to-hf-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-hf-teal-600 to-healthcare-teal text-white rounded-full text-sm font-semibold mb-4">
              Co-Founder & Brand Ambassador
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Dr. Betsy Grunch, MD
            </h2>
            <p className="text-xl text-hf-teal-600 font-semibold">
              Lady Spine Doc • Neurosurgeon • Healthcare Educator
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">
            {/* Left Column - Photo & Stats */}
            <div className="lg:col-span-2">
              {/* Photo Placeholder */}
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-hf-teal-800 to-healthcare-teal mb-6 flex items-center justify-center">
                {/* Ripple decorations */}
                <div className="absolute w-64 h-64 border-2 border-white/10 rounded-full animate-pulse" />
                <div className="absolute w-80 h-80 border-2 border-white/5 rounded-full animate-pulse delay-150" />

                {/* Placeholder content */}
                <div className="text-center text-white z-10 p-6">
                  <div className="text-7xl mb-4">👩‍⚕️</div>
                  <p className="text-xl font-semibold mb-1">Dr. Betsy Grunch</p>
                  <p className="text-sm text-white/80">@ladyspinedoc</p>
                </div>

                {/* Verified badge */}
                <div className="absolute bottom-4 right-4 bg-healthcare-teal text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Verified Physician
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                {credentials.map((cred, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <div className="w-10 h-10 bg-hf-teal-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <cred.icon className="w-5 h-5 text-hf-teal-600" />
                    </div>
                    <div className="text-sm font-semibold text-gray-900">{cred.value}</div>
                    <div className="text-xs text-gray-500">{cred.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Tabs Content */}
            <div className="lg:col-span-3">
              {/* Tab Navigation */}
              <div className="flex gap-2 mb-8 border-b-2 border-gray-200 pb-4">
                {[
                  { id: 'story', label: 'Her Story' },
                  { id: 'vision', label: 'Her Vision' },
                  { id: 'credentials', label: 'Credentials' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                      activeTab === tab.id
                        ? 'bg-hf-teal-600 text-white'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="min-h-[400px]">
                {/* Story Tab */}
                {activeTab === 'story' && (
                  <div className="animate-fadeIn">
                    <h3 className="text-2xl font-bold text-gray-900 mb-5">
                      From Caregiver to Neurosurgeon to Healthcare Innovator
                    </h3>
                    <div className="text-gray-600 leading-relaxed space-y-4">
                      <p>
                        Before she was one of America's top spine surgeons, Dr. Betsy Grunch was a teenager in Gainesville, Georgia, watching her mother—a sheriff's deputy—fight for her life after a devastating on-duty car accident that left her quadriplegic.
                      </p>
                      <p>
                        "That moment changed everything," Dr. Grunch recalls. "I became a certified nursing assistant while still in high school just to help care for her. I saw firsthand how the healthcare system works—and where it falls short."
                      </p>
                      <p>
                        That experience ignited a fire. She earned her degree at the University of Georgia, completed medical school at the Medical College of Georgia, and trained at Duke University Medical Center—eventually returning to Georgia to join the same practice that had treated her mother.
                      </p>
                      <p>
                        Today, as one of only 219 female board-certified neurosurgeons in the United States, Dr. Grunch has built a following of over 4 million people who trust her for honest, expert health guidance. Now, she's channeling that influence into building the platform she wished existed: <strong className="text-hf-teal-600">Healthfluence</strong>.
                      </p>
                    </div>
                  </div>
                )}

                {/* Vision Tab */}
                {activeTab === 'vision' && (
                  <div className="animate-fadeIn">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">
                      Building the Future of Healthcare Marketing
                    </h3>
                    <div className="space-y-4">
                      {quotes.map((quote, i) => (
                        <div
                          key={i}
                          className="bg-white rounded-2xl p-6 border-l-4 border-hf-teal-500 shadow-sm"
                        >
                          <blockquote className="text-gray-700 italic leading-relaxed mb-3">
                            "{quote.text}"
                          </blockquote>
                          <cite className="text-sm text-hf-teal-600 font-semibold not-italic">
                            — Dr. Betsy Grunch, {quote.context}
                          </cite>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Credentials Tab */}
                {activeTab === 'credentials' && (
                  <div className="animate-fadeIn">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">
                      Recognition & Achievements
                    </h3>
                    <div className="space-y-3 mb-8">
                      {accolades.map((accolade, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-hf-teal-500 to-healthcare-teal flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-gray-700">{accolade}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-gradient-to-br from-hf-teal-50 to-healthcare-teal/10 rounded-2xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        Professional Memberships
                      </h4>
                      <ul className="text-gray-700 space-y-2">
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-hf-teal-500 rounded-full" />
                          American College of Surgeons
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-hf-teal-500 rounded-full" />
                          American Association of Neurological Surgeons
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-hf-teal-500 rounded-full" />
                          Congress of Neurological Surgeons
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-hf-teal-500 rounded-full" />
                          Association of Women Surgeons
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founding Quote CTA */}
      <section className="py-20 bg-gradient-to-br from-hf-teal-800 to-hf-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl text-hf-teal-400 mb-6">"</div>
          <blockquote className="text-2xl sm:text-3xl font-medium text-white leading-relaxed mb-8">
            I spent years building trust with millions of followers by being authentic, by being accurate, and by putting patients first. That's not something you can fake—and it's exactly what healthcare brands need. Healthfluence is how we scale that trust.
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-3xl">
              👩‍⚕️
            </div>
            <div className="text-left">
              <div className="text-white font-semibold">Dr. Betsy Grunch, MD, FAANS, FACS</div>
              <div className="text-hf-teal-300 text-sm">Co-Founder & Brand Ambassador, Healthfluence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-hf-teal-600 to-healthcare-teal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Join the Future of Healthcare Marketing?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Whether you're a brand looking for trusted voices or a physician ready to share your expertise—we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register?type=brand"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-hf-teal-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-lg"
            >
              Apply as a Brand
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/register?type=creator"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors text-lg"
            >
              Join as a Creator
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AboutPage
